import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MdChevronLeft } from "react-icons/md";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { Tooltip } from "@mui/material";
import { useAppDispatch } from "src/common/state/hooks";
import { ScenariaPlan } from "src/model/model";
import { IMedia } from "src/model/unityObject";
import SearchableDropdown from "src/common/components/SearchableDropdown/SearchableDropdown";
import InfoIconWithTooltip from "src/common/components/Icon/InfoIconWithTooltip";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import {
  fetchSearchIndex,
  getProjectGroupSize,
  getSearchIndex,
} from "src/features/projectGroups/state/projectGroupsSlice";
import {
  fetchAndUpdateSpecificConversation,
  generateFromPlanThunk,
  getScenarioHelperContextImages,
} from "../../state/chatbotSlice";
import DecorStatusRow from "../referenceImage/DecorStatusRow";
import BlockadeImagesSection from "./BlockadeImagesSection";
import { computeGenerationEstimate } from "./generationEstimate";

// Confirm modal shown when the user leaves the stage with unsaved prompt
// overrides (D10). Local to this surface — overrides are ephemeral by design.
const discardPromptOverridesModalId = "discardPromptOverridesModal";
// Pre-generate recap/confirm modal (D1/D2) — every generation passes through
// it. The Generate button opens it; the actual dispatch fires from its Confirm.
const confirmGenerationModalId = "confirmGenerationModal";

interface ConfigureGenerationPanelProps {
  plan?: ScenariaPlan;
  conversationId: number;
  ragAgentIds: number[];
  onBack: () => void;
  onGenerationStarted: () => void;
  // Reference-image state + actions, owned by Chatbot (the staged image and
  // its integrate/replace/remove flows are conversation-level, not
  // stage-level) and surfaced here through the Blockade provenance row.
  stagedReferenceImage: IMedia | null;
  isReferenceImageBusy: boolean;
  onIntegrateStagedImage: () => void;
  onCancelStagedImage: () => void;
  onPickReferenceImage: () => void;
  onRemoveReferenceImage: (media: IMedia) => void;
}

type Destination = "new" | "assign";
type AuthType = "none" | "simplified" | "classic";

// In-chat "Configure generation" stage — the plan-mode replacement for the
// legacy generation modal (D2, docs/plans/todo/scenaria-configure-
// generation-stage.md). Essentials only (Phase 2): name, destination, access
// mode, plain Blockade checkbox. Phase 3 upgrades the Blockade row to an
// OptionSection with per-scene prompt previews + overrides and adds the
// footer estimate.
const ConfigureGenerationPanel = ({
  plan,
  conversationId,
  ragAgentIds,
  onBack,
  onGenerationStarted,
  stagedReferenceImage,
  isReferenceImageBusy,
  onIntegrateStagedImage,
  onCancelStagedImage,
  onPickReferenceImage,
  onRemoveReferenceImage,
}: ConfigureGenerationPanelProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const searchIndex = useSelector(getSearchIndex);
  // Hydrated conversation images — lets the confirm recap show the decor
  // provenance (thumbnail + filename) instead of restating the checkbox.
  const contextImages = useSelector(getScenarioHelperContextImages);

  // Suggested default name — says WHAT was created, not that it was generated:
  // a brand-new Parcours suggests "Parcours - <date>", a module dropped into an
  // existing Parcours suggests "Module - <date>". The date locale follows the UI
  // language (fr-FR → DD/MM/YY, en-US → MM/DD/YY). The GMT+2 shift mirrors the
  // backend default-name helper (fromParsedMarkdownToInsertionPlan.js).
  //
  // The year is 2-digit on purpose: it buys the room a same-day collision
  // counter needs under the 24-char project/project-group name cap
  // ("Parcours - 27/07/26 (2)" is 23). Anything longer than the cap is rejected
  // by nameError below, so the suggestion must never exceed it.
  const suggestedName = useCallback(
    (dest: Destination) => {
      const prettyGenerationDate = new Intl.DateTimeFormat(i18n.language, {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }).format(new Date(Date.now() + 2 * 60 * 60 * 1000));
      const base = t(
        dest === "assign"
          ? "pages.chat.defaultGeneratedModuleName"
          : "pages.chat.defaultGeneratedCourseName",
        { date: prettyGenerationDate },
      );

      // Same-day disambiguation: the 2nd Parcours generated today suggests
      // "… (2)", the 3rd "… (3)", and tomorrow's date resets the series. Read
      // from the already-loaded org search index — no extra request. Names are
      // display-only (no unique constraint), so this is best-effort: two
      // creators generating concurrently can still land on the same suggestion.
      const takenNames = new Set(
        searchIndex
          .filter((item) => item.type === (dest === "assign" ? "project" : "projectGroup"))
          .map((item) => item.name),
      );
      if (!takenNames.has(base)) return base;
      for (let n = 2; n < 100; n += 1) {
        const candidate = `${base} (${n})`;
        // Never suggest a name the 24-char validator would reject.
        if (candidate.length > 24) break;
        if (!takenNames.has(candidate)) return candidate;
      }
      return base;
    },
    [i18n.language, searchIndex, t],
  );

  const [name, setName] = useState(() => suggestedName("new"));
  // The suggestion follows the destination toggle until the creator types their
  // own name — from then on it's theirs and we never clobber it.
  const [isNameEdited, setIsNameEdited] = useState(false);
  const [destination, setDestination] = useState<Destination>("new");
  const [assignedToProjectGroup, setAssignedToProjectGroup] = useState("");
  // Access mode for a plan-mode-generated Parcours. Mirrors project-group.auth_type;
  // defaults to "none" (anonymous) to preserve the historical ScenarIA behavior.
  const [authType, setAuthType] = useState<AuthType>("none");
  const [generateBlockadeImages, setGenerateBlockadeImages] = useState(false);
  // sceneId -> full replacement prompt. An entry exists ONLY while the user's
  // text differs from the server-composed prompt (D6). Ephemeral: lost on
  // unmount (leaving the stage) — that IS the staleness model, never persist.
  const [promptOverrides, setPromptOverrides] = useState<Record<string, string>>({});
  const [isComputingPgSize, setIsComputingPgSize] = useState(false);
  const [sizeError, setSizeError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const setPromptOverride = (sceneId: string, text: string) =>
    setPromptOverrides((prev) => ({ ...prev, [sceneId]: text }));
  const clearPromptOverride = (sceneId: string) =>
    setPromptOverrides((prev) => {
      if (!(sceneId in prev)) return prev;
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
  const hasOverrides = Object.keys(promptOverrides).length > 0;

  const estimate = computeGenerationEstimate(plan, generateBlockadeImages);

  // Refetch on every entry into the stage, not just when the index is empty:
  // the suggested name's same-day counter reads it, and two generations back to
  // back in one session would otherwise both suggest the same name.
  useEffect(() => {
    dispatch(fetchSearchIndex({}));
  }, [dispatch]);

  useEffect(() => {
    if (!isNameEdited) setName(suggestedName(destination));
  }, [destination, isNameEdited, suggestedName]);

  useEffect(() => {
    if (destination !== "assign" || !assignedToProjectGroup) {
      setSizeError("");
      return;
    }
    const projectIds = searchIndex
      .filter(
        (item) => item.type === "project" && item.projectGroupId === Number(assignedToProjectGroup),
      )
      .map((item) => item.id);
    if (projectIds.length === 0) {
      setSizeError("");
      return;
    }
    setIsComputingPgSize(true);
    setSizeError("");
    dispatch(getProjectGroupSize({ projectsIds: projectIds.join(",") }))
      .then((res: any) => {
        if (res.payload.data > 10737418240) {
          setSizeError(t("tooltip.cannotCreateProjectProjectGroupTooLarge"));
        }
      })
      .finally(() => {
        setIsComputingPgSize(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedToProjectGroup, destination]);

  // Most-recent-first by default. updatedAt may be absent on cached
  // pre-2026-05-06 rows; treat missing as oldest so they sink to the bottom.
  const projectGroupOptions = [...searchIndex]
    .filter((item) => item.type === "projectGroup")
    .sort((a, b) => {
      const aTs = a.updatedAt ? Date.parse(a.updatedAt) : 0;
      const bTs = b.updatedAt ? Date.parse(b.updatedAt) : 0;
      return bTs - aTs;
    });

  // Resolve the assign-target PG id to its display name for the recap modal.
  // SearchableDropdown renders options by `name`; fall back to the raw id.
  const assignedPgName =
    projectGroupOptions.find((o) => String(o.id) === assignedToProjectGroup)?.name ??
    `#${assignedToProjectGroup}`;

  const trimmedName = name.trim();
  const nameError = !trimmedName
    ? t("pages.chat.nameEmptyError")
    : trimmedName.length > 24
    ? t("pages.chat.nameTooLongError")
    : "";

  // A single reason string drives both the disabled state and the tooltip.
  // The in-flight decor realign comes first: it is the only reason here the
  // user cannot see from the form itself, and generating through it silently
  // loses the integration (generation snapshots the pre-realign plan, then the
  // refine's write and the generation's write clobber each other).
  const disabledReason = isReferenceImageBusy
    ? t("pages.chat.configureGeneration.referenceImageBusyReason")
    : nameError
    ? nameError
    : destination === "assign" && !assignedToProjectGroup
    ? t("pages.chat.createProjectFromAiGraph.selectAProjectGroup")
    : isComputingPgSize
    ? t("tooltip.computingPgSize")
    : sizeError
    ? sizeError
    : "";
  const isGenerateDisabled = Boolean(disabledReason) || isGenerating;

  const accessTypeOptionLabel = (opt: AuthType) => {
    switch (opt) {
      case "classic":
        return t("pages.accessTypeTable.passwordLogin");
      case "simplified":
        return t("pages.accessTypeTable.simplifiedLogin");
      default:
        return t("pages.accessTypeTable.noLogin");
    }
  };

  // Back-to-plan only confirms when typed overrides would be lost (D10);
  // zero friction for everyone who never edited a prompt.
  const handleBack = () => {
    if (hasOverrides) {
      dispatch(openStandardModal(discardPromptOverridesModalId));
      return;
    }
    onBack();
  };

  // The Generate button no longer dispatches directly — it opens the recap
  // confirm modal (D1/D2); launchGeneration runs only from the modal's Confirm.
  const handleGenerate = () => {
    if (isGenerateDisabled) return;
    dispatch(openStandardModal(confirmGenerationModalId));
  };

  const launchGeneration = async () => {
    if (isGenerateDisabled) return;
    setIsGenerating(true);

    const genDestination =
      destination === "assign"
        ? { mode: "assignToProjectGroup" as const, projectGroupId: Number(assignedToProjectGroup) }
        : { mode: "newProjectGroup" as const };

    // Overrides only ride along when Blockade is on and at least one exists;
    // the server drops stale/unknown/video360 keys defensively.
    const blockade =
      generateBlockadeImages && hasOverrides
        ? { enabled: true, perScenePromptOverrides: promptOverrides }
        : { enabled: generateBlockadeImages };

    // Dispatch first, then hand back to the thread immediately so the user
    // watches GenerationRecap + the progress strip during the SSE stream
    // rather than staring at a frozen form (the panel unmounts here).
    const pending = dispatch(
      generateFromPlanThunk({
        conversationId,
        ragAgentIds,
        generationOptions: {
          name: trimmedName,
          destination: genDestination,
          authType,
          blockade,
        },
      }),
    );
    onGenerationStarted();

    try {
      const response = (await pending) as any;
      if (response?.error) {
        // Re-sync in case the server completed generation before the SSE dropped.
        dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
        dispatch(
          setAlert({
            msg: response?.payload || t("pages.chat.createProjectFromAiGraph.errorWhileGenerating"),
            type: "error",
            isOpen: true,
            alertShowCloseButton: true,
          }),
        );
        return;
      }
      await dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
      dispatch(
        setAlert({
          msg: t("pages.chat.createProjectFromAiGraph.moduleGeneratedSuccessfully"),
          type: "success",
          isOpen: true,
          customTimeoutInMs: 15000,
        }),
      );
    } catch (error) {
      console.error("Error in ConfigureGenerationPanel handleGenerate:", error);
      dispatch(fetchAndUpdateSpecificConversation({ conversationId }));
      dispatch(
        setAlert({
          msg: t("pages.chat.createProjectFromAiGraph.errorWhileGenerating"),
          type: "error",
          isOpen: true,
          alertShowCloseButton: true,
        }),
      );
    }
  };

  const inputClasses =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500";

  return (
    <div className="bg-gray-100 p-4 flex-grow overflow-y-auto rounded-md flex flex-col space-y-4">
      {/* Header row */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-alternative-outline text-sm inline-flex items-center gap-1"
          onClick={handleBack}
        >
          <MdChevronLeft size={18} className="shrink-0" />
          {t("pages.chat.configureGeneration.backToPlan")}
        </button>
        <h2 className="text-lg font-bold">{t("pages.chat.configureGeneration.title")}</h2>
      </div>

      {/* Essentials — always visible, never collapsible */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-3 space-y-3">
        {/* Name */}
        <div>
          <label htmlFor="configureName" className="text-sm font-medium text-gray-700">
            {t("pages.chat.createProjectFromAiGraph.enterName")}
          </label>
          <input
            id="configureName"
            type="text"
            value={name}
            onChange={(e) => {
              setIsNameEdited(true);
              setName(e.target.value);
            }}
            placeholder={t("pages.chat.createProjectFromAiGraph.enterName")}
            className={inputClasses}
            autoFocus
          />
          <p className="text-red-500 text-xs min-h-[1rem]">{nameError}</p>
        </div>

        {/* Destination */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">
            {t("pages.chat.configureGeneration.destinationLabel")}
          </p>
          {(["new", "assign"] as const).map((opt) => (
            <label key={opt} className="flex items-center gap-2 mb-1 cursor-pointer">
              <input
                type="radio"
                name="configureDestination"
                value={opt}
                checked={destination === opt}
                onChange={() => setDestination(opt)}
                className="h-3.5 w-3.5 cursor-pointer accent-green-600"
              />
              <span className="text-sm text-gray-700">
                {opt === "new"
                  ? t("pages.chat.configureGeneration.destinationNew")
                  : t("pages.chat.configureGeneration.destinationAssign")}
              </span>
            </label>
          ))}
          {destination === "assign" && (
            <div className="mt-2">
              <SearchableDropdown
                options={projectGroupOptions}
                selectedId={assignedToProjectGroup}
                onSelect={(selectedId) => {
                  setAssignedToProjectGroup(selectedId.toString());
                }}
                placeholder={t("pages.chat.createProjectFromAiGraph.selectAProjectGroup")}
                searchPlaceholder={t("pages.chat.createProjectFromAiGraph.searchAProjectGroup")}
                noItemFound={t("pages.chat.createProjectFromAiGraph.noCourseFound")}
              />
              <p className="text-red-500 text-xs min-h-[1rem]">{sizeError}</p>
            </div>
          )}
        </div>

        {/* Access mode — only meaningful for a brand-new Parcours */}
        {destination === "new" && (
          <div>
            <p className="text-sm font-medium mb-2">{t("chooseTemplateType.accessType")}</p>
            {(["none", "simplified", "classic"] as const).map((opt) => (
              <label key={opt} className="flex items-center gap-2 mb-1 cursor-pointer">
                <input
                  type="radio"
                  name="scenariaAuthType"
                  value={opt}
                  checked={authType === opt}
                  onChange={() => setAuthType(opt)}
                  className="h-3.5 w-3.5 cursor-pointer accent-green-600"
                />
                <span className="text-sm text-gray-700">{accessTypeOptionLabel(opt)}</span>
                <InfoIconWithTooltip
                  message={t(`pages.chat.createProjectFromAiGraph.accessTypeHint.${opt}`)}
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Blockade 360° images — collapsible section with per-scene prompt
          previews, trunk/variation color breakdown and editable overrides. */}
      <BlockadeImagesSection
        plan={plan}
        conversationId={conversationId}
        enabled={generateBlockadeImages}
        onToggleEnabled={() => setGenerateBlockadeImages((v) => !v)}
        overrides={promptOverrides}
        onSetOverride={setPromptOverride}
        onClearOverride={clearPromptOverride}
        stagedReferenceImage={stagedReferenceImage}
        isReferenceImageBusy={isReferenceImageBusy}
        onIntegrateStagedImage={onIntegrateStagedImage}
        onCancelStagedImage={onCancelStagedImage}
        onPickReferenceImage={onPickReferenceImage}
        onRemoveReferenceImage={onRemoveReferenceImage}
      />

      {/* Footer — honest scene/image/time estimate + Generate CTA */}
      <div className="pt-3 flex flex-col items-center gap-2">
        <div className="min-h-[1.25rem] flex items-center gap-1.5 text-xs text-gray-500 italic">
          <IoMdInformationCircleOutline className="shrink-0" />
          <span>
            {t("pages.chat.configureGeneration.estimate", {
              sceneCount: estimate.sceneCount,
              imageCount: estimate.imageCount,
              range:
                estimate.imageCount > 0
                  ? t("pages.chat.configureGeneration.estimateRangeWithImages")
                  : t("pages.chat.configureGeneration.estimateRangeNoImages"),
            })}
          </span>
        </div>
        <Tooltip title={disabledReason} disableHoverListener={!disabledReason}>
          <span>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isGenerateDisabled}
              onClick={handleGenerate}
            >
              {t("general.generate")}
            </button>
          </span>
        </Tooltip>
      </div>

      {/* Discard-overrides confirm (D10) — only ever opened when overrides exist. */}
      <ModalStandard id={discardPromptOverridesModalId} modalType="standard" className="w-[28rem]">
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            {t("pages.chat.configureGeneration.discardOverridesBody")}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => dispatch(closeStandardModal(discardPromptOverridesModalId))}
            >
              {t("pages.chat.configureGeneration.discardOverridesStay")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              onClick={() => {
                dispatch(closeStandardModal(discardPromptOverridesModalId));
                onBack();
              }}
            >
              {t("pages.chat.configureGeneration.discardOverridesLeave")}
            </button>
          </div>
        </div>
      </ModalStandard>

      {/* Pre-generate recap/confirm (D1/D2) — always shown; translates the form
          into consequence language so wrong name/destination/forgotten-images
          mistakes surface before the minutes-long, irreversible generation.
          Structured like the plan card (labelled sections over hairline
          dividers) rather than a flat bullet list: destination, then the
          image-generation story (count, decor provenance, edited prompts,
          ignored staged image), then the ends-conversation footnote. */}
      <ModalStandard id={confirmGenerationModalId} modalType="standard" className="w-[36rem]">
        <div className="p-3 space-y-3">
          <p className="text-base font-semibold text-gray-800">
            {t("pages.chat.configureGeneration.confirmTitle")}
          </p>
          <div className="divide-y divide-gray-100">
            <div className="pb-2.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t("pages.chat.configureGeneration.destinationLabel")}
              </h4>
              <p className="text-sm text-gray-700 mt-0.5">
                {destination === "new"
                  ? t("pages.chat.configureGeneration.confirmNewCourse", {
                      name: trimmedName,
                    })
                  : t("pages.chat.configureGeneration.confirmAssignCourse", {
                      name: trimmedName,
                      pgName: assignedPgName,
                    })}
              </p>
              {/* Access mode on its own line — it decides who can open the
                  course, which is too consequential to bury in parentheses.
                  Only a brand-new Parcours carries one (assign inherits). */}
              {destination === "new" && (
                <p className="text-sm text-gray-700 mt-0.5">
                  {t("pages.chat.configureGeneration.confirmAccessType")}{" "}
                  <span className="font-medium">{accessTypeOptionLabel(authType)}</span>
                </p>
              )}
            </div>
            <div className="py-2.5 space-y-1.5">
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {t("pages.chat.configureGeneration.confirmImagesTitle")}
              </h4>
              {generateBlockadeImages ? (
                <>
                  <p className="text-sm text-gray-700">
                    {t("pages.chat.configureGeneration.confirmWithImages", {
                      count: estimate.imageCount,
                    })}
                  </p>
                  {/* Same decor-provenance object as the plan card and the
                      Blockade section — informative-only here. */}
                  <DecorStatusRow plan={plan} contextImages={contextImages} />
                  {hasOverrides && (
                    <p className="text-sm text-gray-700">
                      {t("pages.chat.configureGeneration.confirmOverrides", {
                        count: Object.keys(promptOverrides).length,
                      })}
                    </p>
                  )}
                </>
              ) : (
                // Amber, matching the reference-image warning below: every
                // scenaria plan is image360, so generating without decor is the
                // line a user is most likely to regret overlooking (the silent
                // default-off is how "no images in my scenes" tickets happen).
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  {t("pages.chat.configureGeneration.confirmWithoutImages")}
                </p>
              )}
              {/* A picked-but-not-integrated reference image is a consequence
                  too — the generation will simply ignore it. Amber: this is
                  the one line the user is most likely to regret overlooking.
                  With image generation OFF, the wording continues the
                  "no images" sentence ("ne sera donc pas utilisée") instead of
                  mentioning image generation a second time — the two phrasings
                  side by side read as a contradiction. */}
              {stagedReferenceImage && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  {t(
                    generateBlockadeImages
                      ? "pages.chat.configureGeneration.confirmStagedImageIgnored"
                      : "pages.chat.configureGeneration.confirmStagedImageIgnoredNoGen",
                    {
                      name: stagedReferenceImage.filename || stagedReferenceImage.name,
                    },
                  )}
                </p>
              )}
            </div>
            <div className="pt-2.5">
              <p className="text-xs text-gray-500 italic">
                {t("pages.chat.configureGeneration.confirmEndsConversation")}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => dispatch(closeStandardModal(confirmGenerationModalId))}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isGenerateDisabled}
              onClick={() => {
                dispatch(closeStandardModal(confirmGenerationModalId));
                launchGeneration();
              }}
            >
              {t("general.generate")}
            </button>
          </div>
        </div>
      </ModalStandard>
    </div>
  );
};

export default ConfigureGenerationPanel;
